/**
 * Collection page: product compare selection (max 3, min 2 to enable CTA).
 * Persists selection to sessionStorage and redirects to compare page with payload.
 */
const STORAGE_KEY = 'beyours:product-compare-v1';

function readState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeState(items) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

function getBar() {
  return document.getElementById('ProductCompareBar');
}

function getCardData(card) {
  const d = (s) => card.getAttribute(s) || '';
  return {
    handle: d('data-product-handle'),
    title: d('data-compare-title'),
    priceFormatted: d('data-compare-price') || '',
    colors: card.dataset.compareColors,
    colorSwatches: card.dataset.compareColorSwatches,
    image: d('data-compare-image'),
    r1: d('data-compare-r1'),
    r2: d('data-compare-r2'),
    r3: d('data-compare-r3'),
    r4: d('data-compare-r4'),
    r5: d('data-compare-r5'),
    url: d('data-compare-url'),
  };
}

function syncCheckboxFromState(card, state) {
  const input = card.querySelector('[data-product-compare-input]');
  if (!input) return;
  const handle = card.getAttribute('data-product-handle');
  input.checked = state.some((s) => s.handle === handle);
}

function updateBarUI(bar, state) {
  const list = bar.querySelector('[data-compare-list]');
  const go = bar.querySelector('[data-compare-go]');
  const min = parseInt(bar.getAttribute('data-compare-min') || '2', 10);
  const max = parseInt(bar.getAttribute('data-compare-max') || '3', 10);
  if (!list || !go) return;

  const emptySlotText = bar.getAttribute('data-compare-empty-slot') || '—';
  const removeLabel = bar.getAttribute('data-compare-remove-label') || 'Remove';
  const esc = (s) => (s == null ? '' : String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'));

  list.innerHTML = '';
  for (const item of state) {
    const li = document.createElement('li');
    li.className = 'product-compare-bar__item';
    li.innerHTML = `<span class="product-compare-bar__item-title"></span>
      <button type="button" class="product-compare-bar__remove" data-remove-handle="${esc(item.handle)}" aria-label="${esc(removeLabel)}">×</button>`;
    li.querySelector('.product-compare-bar__item-title').textContent = item.title || item.handle;
    list.appendChild(li);
  }
  for (let i = state.length; i < max; i++) {
    const li = document.createElement('li');
    li.className = 'product-compare-bar__item product-compare-bar__item--empty';
    li.textContent = emptySlotText;
    list.appendChild(li);
  }

  const active = state.length >= min;
  go.setAttribute('aria-disabled', active ? 'false' : 'true');
  go.classList.toggle('is-disabled', !active);
  bar.hidden = state.length === 0;
  document.body.classList.toggle('product-compare--active', state.length > 0);
}

function onCompareGoClick(e) {
  const bar = getBar();
  if (!bar) return;
  if (e.currentTarget.getAttribute('aria-disabled') === 'true' || e.currentTarget.classList.contains('is-disabled')) {
    e.preventDefault();
    return;
  }
  const state = readState();
  const href = bar.getAttribute('data-compare-page') || '/pages/compare';
  writeState(state);
  e.preventDefault();
  window.location.href = href;
}

function removeItem(handle) {
  let state = readState();
  state = state.filter((s) => s.handle !== handle);
  writeState(state);
  document.querySelectorAll('[data-product-compare][data-product-handle="' + handle + '"]').forEach((card) => {
    const input = card.querySelector('[data-product-compare-input]');
    if (input) input.checked = false;
  });
  if (getBar()) updateBarUI(getBar(), state);
}

function toggleHandle(card, add) {
  const data = getCardData(card);
  if (!data.handle) return;
  let state = readState();
  const max = parseInt(getBar()?.getAttribute('data-compare-max') || '3', 10);
  if (add) {
    if (state.some((s) => s.handle === data.handle)) return;
    if (state.length >= max) return;
    state.push(data);
  } else {
    state = state.filter((s) => s.handle !== data.handle);
  }
  writeState(state);
  if (getBar()) updateBarUI(getBar(), state);
}

function initBar() {
  const bar = getBar();
  if (!bar) return;
  const state = readState();
  document.querySelectorAll('[data-product-compare]').forEach((card) => {
    syncCheckboxFromState(card, state);
  });
  updateBarUI(bar, state);

  bar.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-remove-handle]');
    if (rm) {
      e.preventDefault();
      removeItem(rm.getAttribute('data-remove-handle'));
    }
  });

  const go = bar.querySelector('[data-compare-go]');
  if (go) go.addEventListener('click', onCompareGoClick);

  const clear = bar.querySelector('[data-compare-clear]');
  if (clear) {
    clear.addEventListener('click', (e) => {
      e.preventDefault();
      writeState([]);
      document.querySelectorAll('[data-product-compare-input]').forEach((i) => {
        i.checked = false;
      });
      updateBarUI(bar, []);
    });
  }

  const toggle = bar.querySelector('[data-compare-toggle]');
  const icon = bar.querySelector('.compare-icon');
  const panel = bar.querySelector('[data-compare-panel]');

  if (toggle && panel) {

    const setIcon = (expanded) => {
      if (!icon) return;

      icon.src = expanded
        ? 'https://cdn.shopify.com/s/files/1/1772/1101/files/Expand_Arrow.png?v=1778729954'
        : 'https://cdn.shopify.com/s/files/1/1772/1101/files/Close_Arrow.png?v=1778730952';
    };

    const expanded = !panel.hidden;
    setIcon(expanded);
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    toggle.addEventListener('click', () => {
      const willHide = !panel.hidden;
      panel.hidden = willHide;

      const isExpanded = !willHide;
      toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');

      setIcon(isExpanded);
    });
  } 
}

function onCardInputChange(e) {
  const input = e.target;
  if (!input.matches('[data-product-compare-input]')) return;
  const card = input.closest('[data-product-compare]');
  if (!card) return;
  if (!input.checked) {
    toggleHandle(card, false);
    return;
  }
  const min = parseInt(getBar()?.getAttribute('data-compare-min') || '2', 10);
  const max = parseInt(getBar()?.getAttribute('data-compare-max') || '3', 10);
  const state = readState();
  const h = card.getAttribute('data-product-handle');
  if (state.length >= max && !state.some((s) => s.handle === h)) { 
    input.checked = false;
    return;
  }
  toggleHandle(card, true);
}

// Image clicks no longer toggle compare: the image link now opens the product
// page in a new tab (target="_blank" in card-product.liquid). Compare is selected
// only with the checkbox in the card's top-left corner.

function init() {
  if (!getBar()) return;
  initBar();
  document.getElementById('ProductGridContainer')?.addEventListener('change', onCardInputChange);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
